import { InputAdornment, Stack, Typography } from '@mui/material';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import { EmailIcon } from '@/components/Icons/Icons';
import { CustomTextfield } from '@/components/CustomTextfield/styles';
import { DeleteButton, LeftSide, SettingBox, WhiteWrapper } from '../styles';
import theme from '@/theme';
import useModalStore from '@/stores/modalStore';
import DeleteModal from '@/components/CustomModal/DeleteModal';
import { useAuthStore } from '@/stores/authStore';
import useAlertStore from '@/stores/alertStore';
import { FIREBASE_ERRORS } from '@/helpers/errors';
import { useDeleteUser } from '@/services/noteGoApi/noteGoApiComponents';
import { doSignOut } from '@/firebase/auth';
import { useState } from 'react';

const DeleteAccount = () => {
  const { isOpen: isModalOpen, toggleModal } = useModalStore();
  const { currentUser, userToken } = useAuthStore();
  const { showAlert } = useAlertStore();
  const { mutateAsync: mutateDeleteUser } = useDeleteUser();
  const [newEmail, setNewEmail] = useState('');

  const handleOpenDeleteModal = () => {
    toggleModal();
  };

  const handleDeleteAccount = async () => {
    if (!currentUser) {
      showAlert('No user is currently logged in', 'error');
      return;
    }

    try {
      await mutateDeleteUser({
        headers: {
          authorization: `Bearer ${userToken}`,
        },
      });
      showAlert('Your account has been deleted successfully', 'success');
      await doSignOut();
    } catch (error) {
      const typedError = error as { code: string };
      if (typedError.code === FIREBASE_ERRORS.AUTH_REQUIRES_RECENT_LOGIN) {
        showAlert('Please re-login before to delete your account', 'error');
        return;
      }
      showAlert(`Failed to delete account`, 'error');
    }
  };

  return (
    <>
      <SettingBox>
        <LeftSide>
          <Typography variant="body1" fontWeight="700">
            Delete My Account
          </Typography>
        </LeftSide>

        <WhiteWrapper gap="1.5rem">
          <Typography variant="body2" color={theme.palette.neutral.greyFont}>
            We're sorry to see you go. By clicking the 'Delete Account' button, you'll confirm the deletion of your
            account. Please be sure you want to proceed, as you will lose access to all your data, which cannot be
            recovered.
          </Typography>

          <Stack gap="0.5rem">
            <Typography variant="body2" fontWeight="700">
              Your Email Address
            </Typography>
            <CustomTextfield
              variant="outlined"
              size="small"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Stack>

          <DeleteButton
            variant="outlined"
            color="error"
            endIcon={<DeleteForeverOutlinedIcon />}
            onClick={handleOpenDeleteModal}
            disabled={newEmail !== currentUser?.email}
          >
            Delete account
          </DeleteButton>
        </WhiteWrapper>
      </SettingBox>

      <DeleteModal
        open={isModalOpen}
        handleClose={toggleModal}
        text="We're sorry to see you go. By deleting your account, you will lose access to all your data and it cannot be
          recovered. Please make sure you really want to proceed."
        confirmButtonText="Yes, delete account"
        onConfirm={handleDeleteAccount}
      />
    </>
  );
};

export default DeleteAccount;
